import React from "react";
import { Link } from "react-router-dom";
import {
  Play,
  Users,
  Smartphone,
  ArrowRight,
  CheckCircle,
  Target,
  Heart,
  Shield,
  TrendingUp,
  Grid,
  Info,
} from "lucide-react";
import {
  Button,
  FeatureCard,
  TestimonialCard,
  Layout,
  MiniProgramQRCode,
} from "../../../shared/components";
import { placeholders } from "../../../shared/utils/avatars";
import logo from "../../../assets/pacego.png";

const HomePage: React.FC = () => {
  // 静态数据，不依赖API
  const stats = {
    events: 120,
    companions: 85,
    orders: 1200,
  };

  return (
    <Layout>
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative container mx-auto px-4 py-20 md:py-28">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 rounded-full mb-6">
                <span className="text-4xl font-bold">跑</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                RunBuddy 跑伴
              </h1>
              <p className="text-xl md:text-2xl text-orange-100 mb-8 leading-relaxed">
                专业陪跑服务平台
                <br />
                让每一次奔跑都有专业陪伴
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <button onClick={() => alert("请使用微信小程序体验完整功能")}>
                <Button size="lg" icon={Play} iconPosition="left">
                  立即开始
                </Button>
              </button>
              <button onClick={() => alert("请使用微信小程序寻找陪跑员")}>
                <Button
                  variant="outline"
                  size="lg"
                  icon={Users}
                  iconPosition="left"
                >
                  寻找陪跑员
                </Button>
              </button>
            </div>

            {/* Stats Display */}
            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">
                  {stats.events}+
                </div>
                <div className="text-orange-100 text-sm">热门赛事</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">
                  {stats.companions}+
                </div>
                <div className="text-orange-100 text-sm">专业陪跑员</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">
                  98%
                </div>
                <div className="text-orange-100 text-sm">满意度</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute -bottom-1 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              为什么选择RunBuddy？
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              专业的陪跑服务，让你的每次训练都更高效、更安全、更有趣
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <FeatureCard
              title="专业配速"
              description="经验丰富的陪跑员，精准控制配速，助你达成目标时间，提升跑步表现"
              icon={Target}
              iconColor="orange"
            />
            <FeatureCard
              title="安全保障"
              description="专业认证陪跑员，熟悉路线安全，提供应急处理，让你安心享受跑步乐趣"
              icon={Shield}
              iconColor="blue"
            />
            <FeatureCard
              title="心理支持"
              description="专业心理疏导，克服跑步焦虑，提供正向激励，让你爱上跑步运动"
              icon={Heart}
              iconColor="green"
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              简单三步，开启专业陪跑
            </h2>
            <p className="text-xl text-gray-600">操作简单，体验专业</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  选择赛事
                </h3>
                <p className="text-gray-600">
                  浏览热门马拉松赛事，选择你要参加的比赛项目
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  匹配陪跑员
                </h3>
                <p className="text-gray-600">
                  根据目标时间和需求，匹配最适合的专业陪跑员
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  享受陪跑
                </h3>
                <p className="text-gray-600">
                  专业陪跑服务，助你轻松达成PB，享受跑步乐趣
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              成功案例
            </h2>
            <p className="text-xl text-gray-600">真实用户的成功经历</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <TestimonialCard
              name="张先生"
              title="上海马拉松完赛者"
              avatar={placeholders.userAvatar("张先生")}
              rating={5}
              content="第一次参加全马，在RunBuddy陪跑员的帮助下，成功在4小时内完赛！配速控制得很好，全程都很轻松。"
              achievement="上海马拉松完赛"
              personalBest="3:58:42"
            />
            <TestimonialCard
              name="李女士"
              title="北京马拉松完赛者"
              avatar={placeholders.userAvatar("李女士")}
              rating={5}
              content="陪跑员不仅帮我控制配速，还在心理上给了我很大支持。现在我已经爱上了跑步，准备挑战更多赛事！"
              achievement="北京马拉松完赛"
              personalBest="4:15:33"
            />
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              快速导航
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Link
                to="/about"
                className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-8 hover:shadow-xl transition-all group"
              >
                <Info className="w-12 h-12 text-blue-500 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold mb-3">产品介绍</h3>
                <p className="text-gray-600 mb-4">
                  了解跑伴平台的核心功能和技术特色
                </p>
                <div className="flex items-center text-blue-600 font-medium">
                  了解更多 <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>

              <Link
                to="/investor"
                className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-2xl p-8 hover:shadow-xl transition-all group"
              >
                <TrendingUp className="w-12 h-12 text-orange-500 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold mb-3">投资人演示</h3>
                <p className="text-gray-600 mb-4">
                  查看商业模式、财务预测和投资回报
                </p>
                <div className="flex items-center text-orange-600 font-medium">
                  查看演示 <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>

              <Link
                to="/portal"
                className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-8 hover:shadow-xl transition-all group"
              >
                <Grid className="w-12 h-12 text-green-500 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold mb-3">系统入口</h3>
                <p className="text-gray-600 mb-4">
                  访问小程序、管理后台和API文档
                </p>
                <div className="flex items-center text-green-600 font-medium">
                  进入系统 <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-red-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              准备好开始你的专业跑步之旅了吗？
            </h2>
            <p className="text-xl text-orange-100 mb-8 leading-relaxed">
              加入RunBuddy，找到最适合你的专业陪跑员，让每一次训练都更高效
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link to="/portal">
                <Button size="lg" icon={Grid} iconPosition="left">
                  访问系统
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  size="lg"
                  icon={Users}
                  iconPosition="left"
                >
                  联系我们
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Smartphone className="w-16 h-16 text-orange-500 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                下载RunBuddy小程序
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                微信扫码即可使用，随时随地预约专业陪跑服务
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="bg-white rounded-2xl p-8">
                <MiniProgramQRCode size="large" showTitle={false} />
              </div>

              <div className="space-y-6">
                <div className="grid gap-4">
                  <div className="flex items-center bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                    <CheckCircle className="w-6 h-6 text-green-400 mr-3 flex-shrink-0" />
                    <span className="text-gray-100">无需下载APP，微信扫码即用</span>
                  </div>
                  <div className="flex items-center bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                    <CheckCircle className="w-6 h-6 text-green-400 mr-3 flex-shrink-0" />
                    <span className="text-gray-100">微信一键登录，快速注册</span>
                  </div>
                  <div className="flex items-center bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                    <CheckCircle className="w-6 h-6 text-green-400 mr-3 flex-shrink-0" />
                    <span className="text-gray-100">微信支付，安全便捷</span>
                  </div>
                  <div className="flex items-center bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
                    <CheckCircle className="w-6 h-6 text-green-400 mr-3 flex-shrink-0" />
                    <span className="text-gray-100">实时消息通知，不错过动态</span>
                  </div>
                </div>

                <div className="bg-orange-500 bg-opacity-20 rounded-lg p-4 border border-orange-500">
                  <p className="text-sm text-orange-100">
                    💡 <strong>提示：</strong>也可以在微信中直接搜索"跑伴"小程序
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <img 
                src={logo} 
                alt="跑伴Logo" 
                className="w-16 h-16 mx-auto mb-4 object-contain"
              />
              <h3 className="text-2xl font-bold text-gray-900">
                RunBuddy 跑伴
              </h3>
              <p className="text-gray-600 mt-2">专业陪跑服务平台</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 text-sm text-gray-600 mb-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">服务项目</h4>
                <div className="space-y-2">
                  <div>专业陪跑</div>
                  <div>配速指导</div>
                  <div>心理支持</div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">热门赛事</h4>
                <div className="space-y-2">
                  <div>北京马拉松</div>
                  <div>上海马拉松</div>
                  <div>杭州马拉松</div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">联系我们</h4>
                <div className="space-y-2">
                  <div>客服热线</div>
                  <div>在线客服</div>
                  <div>意见反馈</div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
              <p className="text-gray-500 text-sm">
                © 2024 RunBuddy 跑伴. 让每一次奔跑都有专业陪伴.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </Layout>
  );
};

export default HomePage;
